import * as React from "react";
import {
  Container,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Paper,
  Chip,
  Link as MUILink
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";


export default function PrivacyPolicy() {
  const Section = ({ title, children, defaultExpanded = false }) => (
    <Accordion defaultExpanded={defaultExpanded} elevation={0} sx={{
      bgcolor: "transparent",
      border: (theme) => `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      mb: 2,
      overflow: "hidden"
    }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6" fontWeight={700}>{title}</Typography>
      </AccordionSummary>
      <Divider />
      <AccordionDetails>
        <Stack spacing={1.5}>{children}</Stack>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
        <Stack spacing={2}>
          <Typography variant="h3" fontWeight={800}>Snapshot Videos Privacy Policy</Typography>
          <Chip label="Last Updated: September 5, 2025" variant="outlined" />

          <Typography color="text.secondary">
            Snapshot Videos ("we," "our," or "us") values your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, services, and applications (collectively, the “Services”). By using Snapshot Videos, you agree to the practices described in this Privacy Policy.
          </Typography>

          <Section title="1. Information We Collect" defaultExpanded>
            <List dense>
              <ListItem><ListItemText primary="Personal Information: Name, email address, phone number, billing details, and organization details provided when you sign up for our services, create an account, or contact us." /></ListItem>
              <ListItem><ListItemText primary="Project Information: Content, files, newsletters, and other materials you provide to create Snapshot Videos." /></ListItem>
              <ListItem><ListItemText primary="Usage Data: IP address, browser type, operating system, pages visited, time spent, and cookies." /></ListItem>
              <ListItem><ListItemText primary="Payment Information: Processed securely through third-party payment providers (e.g., Stripe). We do not store full credit card numbers." /></ListItem>
            </List>
          </Section>

          <Section title="2. How We Use Your Information">
            <List dense>
              <ListItem><ListItemText primary="Provide and improve our Services." /></ListItem>
              <ListItem><ListItemText primary="Manage your account and video projects." /></ListItem>
              <ListItem><ListItemText primary="Communicate with you about updates, promotions, and customer support." /></ListItem>
              <ListItem><ListItemText primary="Analyze usage trends to enhance user experience." /></ListItem>
              <ListItem><ListItemText primary="Comply with legal obligations and prevent fraudulent activity." /></ListItem>
            </List>
          </Section>

          <Section title="3. Sharing of Information">
            <List dense>
              <ListItem><ListItemText primary="Service Providers: Payment processors, hosting services, analytics providers, and email services." /></ListItem>
              <ListItem><ListItemText primary="Team Members: Authorized employees or contractors involved in your video projects." /></ListItem>
              <ListItem><ListItemText primary="Legal Requirements: If required by law, regulation, or legal process." /></ListItem>
              <ListItem><ListItemText primary="Business Transfers: In the event of a merger, acquisition, or sale of assets." /></ListItem>
            </List>
          </Section>

          <Section title="4. Cookies & Tracking Technologies">
            <Typography>We use cookies and similar technologies to:</Typography>
            <List dense>
              <ListItem><ListItemText primary="Remember your preferences." /></ListItem>
              <ListItem><ListItemText primary="Improve website performance." /></ListItem>
              <ListItem><ListItemText primary="Analyze how users interact with our Services." /></ListItem>
            </List>
            <Typography>You may disable cookies in your browser, but some features may not function properly.</Typography>
          </Section>

          <Section title="5. Data Security">
            <Typography>We use reasonable physical, technical, and administrative safeguards to protect your information. However, no method of transmission over the Internet or electronic storage is 100% secure.</Typography>
          </Section>

          <Section title="6. Your Rights">
            <List dense>
              <ListItem><ListItemText primary="Access the personal information we hold about you." /></ListItem>
              <ListItem><ListItemText primary="Request corrections or updates." /></ListItem>
              <ListItem><ListItemText primary="Request deletion of your personal information." /></ListItem>
              <ListItem><ListItemText primary="Opt-out of marketing communications at any time." /></ListItem>
            </List>
            <Typography>To exercise these rights, contact us at: <MUILink href="mailto:legal@snapshotvideos.com">legal@snapshotvideos.com</MUILink>.</Typography>
          </Section>

          <Section title="7. Children’s Privacy">
            <Typography>Our Services are not directed toward individuals under 13. We do not knowingly collect personal information from children. If we learn that we have inadvertently collected such data, we will delete it.</Typography>
          </Section>

          <Section title="8. Third-Party Links">
            <Typography>Our Services may contain links to third-party websites. We are not responsible for the privacy practices of those websites.</Typography>
          </Section>

          <Section title="9. International Users">
            <Typography>If you are accessing Snapshot Videos from outside the United States, your information may be transferred and processed in the U.S.</Typography>
          </Section>

          <Section title="10. Changes to This Privacy Policy">
            <Typography>We may update this Privacy Policy from time to time. Changes will be posted on this page with a revised “Last Updated” date.</Typography>
          </Section>

          <Section title="11. Contact Us">
            <Typography>If you have any questions or concerns about this Privacy Policy, contact us at:</Typography>
            <List dense>
              <ListItem><ListItemText primary="Snapshot Videos" /></ListItem>
              <ListItem><ListItemText primary="Email: legal@snapshotvideos.com" /></ListItem>
              <ListItem><ListItemText primary="Phone: (800) 820-6020" /></ListItem>
            </List>
          </Section>

          <Divider />
          <Typography variant="caption" color="text.secondary">
            This Privacy Policy is provided for Snapshot Videos users and may be updated to reflect changes in practices or legal requirements.
          </Typography>
        </Stack>
      </Paper>
    </Container>
  );
}
